'use es6';

import { Component } from 'react';
import withEditorActionsAndEvents from 'ContentEditorUI/components/hoc/withEditorActionsAndEvents';
import { connect } from 'react-redux';
import { setIframeLoading as setIframeLoadingAction } from 'ContentEditorUI/redux/actions/appActions';
import { showPreviewLoadingOverlay, hidePreviewLoadingOverlay, showSidebarIframeSpinner } from 'ContentEditorUI/lib/layoutDom';
import logSeleniumEvent from 'ContentEditorUI/utils/logSeleniumEvent';
import { updateInpageMode as updateInpageModeAction } from 'ContentEditorUI/redux/actions/inpageActions';
import { setIframeHidden as setIframeHiddenAction } from 'ContentEditorUI/redux/actions/appActions';
import { getIframeCaresAboutNextWindowClick, getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getPreviewDomain, getPreviewIframeUrl, getSidebarIframeUrl } from 'ContentEditorUI/redux/selectors/iframesSelectors';
import { getSelectedItemId, getHasSelectionState } from 'ContentEditorUI/redux/selectors/selectionSelectors';
import { getCurrentTabId } from 'ContentEditorUI/redux/selectors/routingSelectors';
import { getPreviewRefreshUrl } from 'ContentEditorUI/lib/inpage/utils';
import { signIfTemplateMulti } from 'ContentEditorUI/lib/preview/utils';
import { getIsFocusModeOn } from '../redux/selectors/localCachedSettingsSelectors';
import EditorConfigSingleton from 'ContentEditorUI/EditorConfigSingleton';
import { getPreviewIframeWrapperElement } from 'ContentEditorUI/lib/layoutDom';
import inpageActionTypes from 'ContentEditorUI/redux/actions/inpageActionTypes';
const mapStateToProps = (state, ownProps) => {
  var _ownProps$canToggleRe;
  const selectedItemId = getHasSelectionState(state) ? getSelectedItemId(state) : null;

  // TODO refactor this to be an experimentalFeature flag
  // Issue: https://git.hubteam.com/HubSpot/ContentEditorUI/issues/17667
  const isFocusModeFeatureEnabled = EditorConfigSingleton.getIsOnScalableEditor() && EditorConfigSingleton.get('features')['focusMode'];
  const canToggleReadOnlyMode = (_ownProps$canToggleRe = ownProps.canToggleReadOnlyMode) !== null && _ownProps$canToggleRe !== void 0 ? _ownProps$canToggleRe : true;
  return {
    currentTabId: getCurrentTabId(state),
    previewDomain: getPreviewDomain(state),
    previewIframeUrl: getPreviewIframeUrl(state),
    selectedItemId,
    sidebarIframeUrl: getSidebarIframeUrl(state),
    iframeCaresAboutNextWindowClick: getIframeCaresAboutNextWindowClick(state),
    isFocusModeOn: isFocusModeFeatureEnabled && getIsFocusModeOn(state),
    isReadOnlyMode: canToggleReadOnlyMode && getIsReadOnlyMode(state)
  };
};
const mapDispatchToProps = {
  setIframeLoading: setIframeLoadingAction,
  updateInpageMode: updateInpageModeAction,
  setIframeHidden: setIframeHiddenAction
};

/**
 * @param {Object} props
 * @param {Object} props.bus (required)
 * @param {string} props.previewDomain (required)
 * @param {string} props.previewIframeUrl (required)
 * @param {string} [props.sidebarIframeUrl] (optional)
 * @param {boolean} props.iframeCaresAboutNextWindowClick (required)
 * @param {Function} props.setIframeLoading (required)
 * @param {boolean} [props.showAndHideLoadingOverlay] (optional)
 * @param {string} [props.selectedItemId] (optional)
 * @param {Function} [props.setIframeHidden] (optional)
 * @param {boolean} [props.isFocusModeOn] (optional)
 * @param {boolean} [props.isReadOnlyMode] (optional)
 * @param {boolean} [props.canToggleReadOnlyMode] (optional)
 */
class EditorIframesCoordinator extends Component {
  constructor(props) {
    super(props);
    this.onPreviewFrameLoaded = () => {
      const {
        isFocusModeOn,
        showAndHideLoadingOverlay
      } = this.props;
      // If focus mode is on, we want to wait to hide the preview overlay
      // until focus mode is finished setting up. We will then hide the
      // previewOverlay via the event bus's post:hideFocusModeLoadingOverlay event
      if (!isFocusModeOn && showAndHideLoadingOverlay) {
        hidePreviewLoadingOverlay();
      }
    };
    this.getReadyToReloadPreviewIframe = () => {
      const {
        setIframeLoading,
        bus,
        showAndHideLoadingOverlay,
        selectedItemId
      } = this.props;
      showSidebarIframeSpinner();
      bus.ready = false;
      setIframeLoading({
        scrollIfAnyBoundsOutOfView: !!selectedItemId
      });
      logSeleniumEvent('startIframeReload');
      if (showAndHideLoadingOverlay) {
        showPreviewLoadingOverlay();
      }
      bus.postMessage({
        action: 'previewNeedsReload'
      });
    };
    this.onRefreshInpageFrame = message => {
      const {
        scrollX,
        scrollY
      } = message;
      const {
        previewIframeUrl
      } = this.props;
      const previewRefreshUrl = getPreviewRefreshUrl(previewIframeUrl, scrollX, scrollY);
      return signIfTemplateMulti(previewRefreshUrl).then(url => {
        try {
          var _this$previewIframe$c, _this$previewIframe$c2;
          (_this$previewIframe$c = this.previewIframe.contentWindow) === null || _this$previewIframe$c === void 0 || (_this$previewIframe$c = _this$previewIframe$c.hubspot) === null || _this$previewIframe$c === void 0 || (_this$previewIframe$c2 = _this$previewIframe$c.detachInpageRedirectDetector) === null || _this$previewIframe$c2 === void 0 || _this$previewIframe$c2.call(_this$previewIframe$c);
        } catch (e) {
          // cross-origin — no-op
        }
        this.previewIframe.src = url;
      });
    };
    this.tellIframeSidebarClicked = () => {
      const {
        bus
      } = this.props;
      bus.postMessage({
        action: 'sidebarClicked'
      });
    };
    this.onWindowClick = () => {
      const {
        iframeCaresAboutNextWindowClick,
        bus
      } = this.props;
      if (iframeCaresAboutNextWindowClick) {
        bus.postMessage({
          action: 'sidebarClicked'
        });
      }
    };
    this.scrollPreviewLeft = ({
      message
    }) => {
      const previewIframeWrapperElement = getPreviewIframeWrapperElement();
      previewIframeWrapperElement.scrollLeft += message.deltaX;
    };
    this.previewIframe = document.querySelector('iframe#contentEditor-preview');
    this.sidebarIframe = document.querySelector('iframe#contentEditor-sidebar');
    this.setupDomEvents();
    this.setupPreviewFrameIntersectionObserver();

    // If the iframe src URLs have alredy been set, setup events and messaging as early as possible
    if (this.hasIframeStartedLoading()) {
      this.setupPreviewFrameDomEvents();
      this.setupMessagingToFrame();
    }
  }
  componentDidMount() {
    const {
      previewIframeUrl,
      sidebarIframeUrl
    } = this.props;

    // If a customer went straight to route that doesn't early load the iframes, the preview-key
    // API failed, or other early logic busted then load up the iframes again.
    if (!this.hasIframeStartedLoading() && previewIframeUrl) {
      void signIfTemplateMulti(previewIframeUrl).then(url => {
        this.startingTheIframesAgain({
          previewIframeUrl: url,
          sidebarIframeUrl
        });
      });
    }
  }
  componentDidUpdate(prevProps) {
    const {
      isReadOnlyMode
    } = this.props;
    if (prevProps.isReadOnlyMode && !isReadOnlyMode) {
      this.turnOffReadOnlyMode();
    } else if (!prevProps.isReadOnlyMode && isReadOnlyMode) {
      this.turnOnReadOnlyMode();
    }
  }
  turnOffReadOnlyMode() {
    const {
      previewIframeUrl,
      sidebarIframeUrl,
      bus
    } = this.props;
    bus.bindInitializationEvents();
    const cacheBustedUrl = getPreviewRefreshUrl(previewIframeUrl, 0, 0);
    return signIfTemplateMulti(cacheBustedUrl).then(url => {
      this.startingTheIframesAgain({
        previewIframeUrl: url,
        sidebarIframeUrl
      });
    });
  }
  turnOnReadOnlyMode() {
    const {
      bus
    } = this.props;
    bus.removeBoundInitializationEvents();
    this.getReadyToReloadPreviewIframe();
  }
  hasIframeStartedLoading() {
    return !!window.hsPreviewIframeStartTimestamp; // early request logic global
  }
  startingTheIframesAgain({
    previewIframeUrl,
    sidebarIframeUrl
  }) {
    window.hsPreviewIframeStartTimestamp = Date.now();
    this.previewIframe.src = previewIframeUrl;
    this.setupPreviewFrameDomEvents();
    this.setupPreviewFrameIntersectionObserver();
    this.setupMessagingToFrame();
    if (sidebarIframeUrl) {
      this.sidebarIframe.src = sidebarIframeUrl;
    }
  }
  setupDomEvents() {
    document.addEventListener('mousedown', this.onWindowClick);
  }
  setupPreviewFrameDomEvents() {
    // If preview frame load event already fired before react started up, call the onload handler now
    if (window.hsPreviewIframeLoaded) {
      this.onPreviewFrameLoaded();
    }

    // Also bind future load events for when refreshes are required
    this.previewIframe.addEventListener('load', this.onPreviewFrameLoaded);
  }

  // This method tells the CEUI event bus to set up interframe / post messaging with the preview frame
  setupMessagingToFrame() {
    const {
      bus,
      previewDomain,
      isReadOnlyMode
    } = this.props;
    if (isReadOnlyMode) {
      return;
    }
    bus.setupMessagingToFrame(this.previewIframe.contentWindow, previewDomain);

    // Listen for events where something in the app wants to refresh the iframe
    bus.on('preview:refresh', this.getReadyToReloadPreviewIframe);

    // Listen for events where inpage is has finished its teardowns and is ready to refresh
    bus.on('post:refreshInpageFrame', this.onRefreshInpageFrame);

    // Listen for events where a click occured in the add module sidebar iframe
    bus.on('post:tellIframeSidebarClicked', this.tellIframeSidebarClicked);

    // Listen for events where a drag triggered an auto-horizontal scroll
    bus.on(`post:${inpageActionTypes.SCROLL_PREVIEW_LEFT_ACTION}`, this.scrollPreviewLeft);
  }
  setupPreviewFrameIntersectionObserver() {
    const {
      setIframeHidden
    } = this.props;
    if (this.previewFrameObserver) {
      this.previewIframeObserver.observe(this.previewIframe);
      return;
    }
    this.previewIframeObserver = new IntersectionObserver(entries => {
      if (entries[0]) {
        setIframeHidden(!entries[0].isIntersecting);
      }
    });
    this.previewIframeObserver.observe(this.previewIframe);
  }
  render() {
    return null;
  }
}
EditorIframesCoordinator.defaultProps = {
  showAndHideLoadingOverlay: true,
  isFocusModeFeatureEnabled: false,
  canToggleReadOnlyMode: true
};
export default connect(mapStateToProps, mapDispatchToProps)(withEditorActionsAndEvents(EditorIframesCoordinator));